import { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { Linkedin, Mail, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/about`).then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  return (
    <section id="about" className="section-padding bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center"
        >
          {/* Image */}
          <div className="relative">
            <div className="aspect-[4/5] overflow-hidden bg-lab-bg-light">
              <img
                src={data.image_url}
                alt={`${data.name} – ${data.title}`}
                loading="lazy"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-lab-accent -z-10 hidden lg:block" />
          </div>

          {/* Content */}
          <div>
            <span className="label-text">Über mich</span>
            <h2 className="font-playfair text-3xl lg:text-4xl font-semibold text-lab-primary mt-4 mb-2">
              {data.name}
            </h2>
            {data.title && (
              <p className="text-lab-accent font-medium mb-6">{data.title}</p>
            )}
            <p className="text-lab-text-muted leading-relaxed mb-4">{data.paragraph_1}</p>
            <p className="text-lab-text-muted leading-relaxed mb-6">{data.paragraph_2}</p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 py-6 border-y border-lab-border mb-6">
              <div>
                <p className="font-playfair text-2xl font-semibold text-lab-primary">{data.stat_1_value}</p>
                <p className="text-sm text-lab-text-muted mt-1">{data.stat_1_label}</p>
              </div>
              <div>
                <p className="font-playfair text-2xl font-semibold text-lab-primary">{data.stat_2_value}</p>
                <p className="text-sm text-lab-text-muted mt-1">{data.stat_2_label}</p>
              </div>
              <div>
                <p className="font-playfair text-2xl font-semibold text-lab-primary">{data.stat_3_value}</p>
                <p className="text-sm text-lab-text-muted mt-1">{data.stat_3_label}</p>
              </div>
            </div>

            {/* Links */}
            <div className="flex items-center gap-4">
              <a
                href="#scheduler"
                className="inline-flex items-center gap-2 bg-lab-primary text-white px-6 py-3 text-sm font-medium hover:bg-lab-accent hover:text-lab-primary transition-colors"
                data-testid="about-cta"
              >
                {data.cta_text} <ArrowRight className="w-4 h-4" />
              </a>
              {data.linkedin_url && (
                <a
                  href={data.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-lab-border flex items-center justify-center text-lab-text-muted hover:text-lab-accent hover:border-lab-accent transition-colors"
                  data-testid="about-linkedin"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
              )}
              {data.email && (
                <a
                  href={`mailto:${data.email}`}
                  className="w-10 h-10 border border-lab-border flex items-center justify-center text-lab-text-muted hover:text-lab-accent hover:border-lab-accent transition-colors"
                  data-testid="about-email"
                >
                  <Mail className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
